/**
 * PayPalButton Component
 *
 * Wrapper component for PayPal Buttons SDK integration.
 * Handles order creation, payment approval, cancellation, and errors.
 * Used for one-time (monthly) payments.
 *
 * @module components/subscription/PayPalButton
 */

import { useEffect, useRef, useCallback } from 'react';
import { PAYPAL_BUTTON_STYLE, PAYPAL_EXPERIENCE } from '../../hooks/usePayPalScript';

/**
 * PayPal order data returned after successful capture
 */
export interface PayPalOrderData {
  orderId: string;
  captureId: string;
  amount: string;
  payerEmail?: string;
  payerName?: string;
}

/**
 * Props for the PayPalButton component
 */
export interface PayPalButtonProps {
  /** User ID to link the payment to */
  userId: string;
  /** Price amount in EUR */
  price: number;
  /** Callback when payment is successfully captured */
  onSuccess: (orderData: PayPalOrderData) => void;
  /** Callback when user cancels the payment */
  onCancel: () => void;
  /** Callback when an error occurs */
  onError: (error: Error) => void;
  /** Whether the button is enabled (terms accepted) */
  enabled: boolean;
}

/**
 * Type for the PayPal global object
 */
interface PayPalNamespace {
  Buttons: (config: PayPalButtonsConfig) => PayPalButtonsInstance;
}

interface PayPalButtonsConfig {
  style?: {
    layout?: 'vertical' | 'horizontal';
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
    shape?: 'rect' | 'pill';
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment';
  };
  createOrder: (
    data: Record<string, unknown>,
    actions: PayPalOrderActions
  ) => Promise<string>;
  onApprove: (
    data: PayPalApproveData,
    actions: PayPalOrderActions
  ) => Promise<void>;
  onCancel?: (data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
}

interface PayPalOrderActions {
  order: {
    create: (orderData: PayPalOrderCreateData) => Promise<string>;
    capture: () => Promise<PayPalCaptureDetails>;
  };
}

interface PayPalOrderCreateData {
  purchase_units: Array<{
    amount: {
      value: string;
      currency_code: string;
    };
    reference_id?: string;
  }>;
  application_context?: {
    brand_name?: string;
    user_action?: string;
    locale?: string;
  };
}

interface PayPalApproveData {
  orderID: string;
  payerID?: string;
}

interface PayPalCaptureDetails {
  id: string;
  status: string;
  payer?: {
    email_address?: string;
    name?: {
      given_name?: string;
      surname?: string;
    };
  };
  purchase_units?: Array<{
    payments?: {
      captures?: Array<{
        id: string;
        amount?: {
          value: string;
          currency_code: string;
        };
      }>;
    };
  }>;
}

interface PayPalButtonsInstance {
  render: (container: HTMLElement | string) => Promise<void>;
  close: () => void;
}

/**
 * Get the PayPal global namespace
 */
function getPayPal(): PayPalNamespace | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).paypal || null;
}

/**
 * PayPalButton Component
 *
 * Renders PayPal payment buttons when enabled and the SDK is loaded.
 */
export function PayPalButton({
  userId,
  price,
  onSuccess,
  onCancel,
  onError,
  enabled,
}: PayPalButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const priceString = price.toFixed(2);

  /**
   * Create PayPal order with the Premium subscription amount
   */
  const createOrder = useCallback(
    async (
      _data: Record<string, unknown>,
      actions: PayPalOrderActions
    ): Promise<string> => {
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: priceString,
              currency_code: 'EUR',
            },
            reference_id: userId,
          },
        ],
        application_context: {
          brand_name: PAYPAL_EXPERIENCE.brand_name,
          user_action: PAYPAL_EXPERIENCE.user_action,
          locale: PAYPAL_EXPERIENCE.locale,
        },
      });
    },
    [userId, priceString]
  );

  /**
   * Handle successful payment approval
   */
  const handleApprove = useCallback(
    async (
      data: PayPalApproveData,
      actions: PayPalOrderActions
    ): Promise<void> => {
      try {
        const details = await actions.order.capture();

        const captureId =
          details.purchase_units?.[0]?.payments?.captures?.[0]?.id || details.id;

        const payerEmail = details.payer?.email_address;
        const payerName = details.payer?.name
          ? `${details.payer.name.given_name || ''} ${details.payer.name.surname || ''}`.trim()
          : undefined;

        const capturedAmount =
          details.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value || priceString;

        onSuccess({
          orderId: data.orderID,
          captureId,
          amount: capturedAmount,
          payerEmail,
          payerName,
        });
      } catch (error) {
        console.error('PayPal capture error:', error);
        onError(
          error instanceof Error
            ? error
            : new Error('Erreur lors de la capture du paiement')
        );
      }
    },
    [onSuccess, onError, priceString]
  );

  /**
   * Handle payment cancellation
   */
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /**
   * Handle PayPal errors
   */
  const handleError = useCallback(
    (error: Error) => {
      console.error('PayPal error:', error);
      onError(error);
    },
    [onError]
  );

  /**
   * Render PayPal buttons when enabled and SDK is loaded
   */
  useEffect(() => {
    if (!enabled || !containerRef.current) {
      return;
    }

    const paypal = getPayPal();
    if (!paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    const container = containerRef.current;
    container.innerHTML = '';

    const buttonsConfig: PayPalButtonsConfig = {
      style: PAYPAL_BUTTON_STYLE,
      createOrder,
      onApprove: handleApprove,
      onCancel: handleCancel,
      onError: handleError,
    };

    let buttonsInstance: PayPalButtonsInstance | null = null;

    try {
      buttonsInstance = paypal.Buttons(buttonsConfig);

      if (container && buttonsInstance) {
        buttonsInstance.render(container).catch((err: Error) => {
          console.error('PayPal button render error:', err);
        });
      }
    } catch (error) {
      console.error('PayPal Buttons initialization error:', error);
    }

    return () => {
      if (buttonsInstance) {
        try {
          buttonsInstance.close();
        } catch {
          // Ignore errors during cleanup
        }
      }
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [enabled, createOrder, handleApprove, handleCancel, handleError]);

  if (!enabled) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-h-[150px]"
      data-testid="paypal-button-container"
      aria-label="Boutons de paiement PayPal"
    />
  );
}

export default PayPalButton;
